import { upperFirst } from "scule";
import type { FetchError } from "ofetch";
import type { RouteMiddleware } from "#app";
import templatesMeta from "#build/apollo-templates-meta.mjs";
import {
  $headlessResource,
  defineNuxtRouteMiddleware,
  isInitialClientLoad,
  preloadComponents,
  useHeadlessDataState,
  useHeadlessDataErrorState,
  useNuxtApp,
  useRouter,
} from "#imports";
import {
  namedMiddleware,
} from "#build/middleware.mjs";
import type { RouteLocationNormalized } from "#vue-router";

function handleLayoutChange ( meta: TemplateMetaOptions ) {
  // We have to check if a layout has been set inside meta.middleware just before the route is resolved
  const unsub = useRouter().beforeResolve( async ( to ) => {
    const newLayout = to.meta.layout;

    // Only set the meta.layout if we haven't already called setPageLayout
    if ( !newLayout ) {
      if ( meta.layout ) {
        to.meta.layout = meta.layout;
      }
    }

    // meta.layout is ignored if we call setPageLayout
    if ( newLayout && meta.layout ) {
      console.warn( "Page layout has been set in middleware and in layout property, ignoring layout property." );
    }

    unsub();
  } );
}

export default defineNuxtRouteMiddleware( async ( to: RouteLocationNormalized, from: RouteLocationNormalized ) => {
  // This guard stops API calls from being made twice, one on the server and
  // one on the client, on initial page load.
  if ( isInitialClientLoad() ) {
    return;
  }

  const headlessState = useHeadlessDataState();
  const headlessErrorState = useHeadlessDataErrorState();
  const { getByRoute } = $headlessResource();

  const nuxtApp = useNuxtApp();

  if ( !to.fullPath.match( /\.[a-zA-Z]{1,4}$/ ) ) { // filter out static file requests
    // Get current page info and template from CMS
    try {
      const data = await getByRoute( to.path );

      if ( data ) {
        headlessState.value = data;
        headlessErrorState.value = null;

        const subType = data.subType;
        if ( !subType ) {
          throw createError( "No subType found" );
        }

        await preloadComponents( `LazyTemplate${upperFirst( subType )}` );

        type DynamicImport<T> = Record<string, () => Promise<{
          default: T;
        }>>;

        const metaOptions = templatesMeta as DynamicImport<TemplateMetaOptions>;

        const metaOptionsKey = subType.toLowerCase();
        if ( metaOptions && metaOptions[metaOptionsKey] ) {
          const meta = ( await metaOptions[metaOptionsKey]() ).default;

          let layoutChanged = false;

          if ( meta.middleware ) {
            let middlewareFunctions = [];

            if ( Array.isArray( meta.middleware ) ) {
              middlewareFunctions = meta.middleware;
            } else {
              middlewareFunctions = [meta.middleware];
            }

            for ( const middleware of middlewareFunctions ) {
              let func;
              if ( typeof middleware === "string" ) {
                const middlewareImport = ( namedMiddleware as DynamicImport<RouteMiddleware> )[middleware];
                func = nuxtApp._middleware.named[middleware] || await middlewareImport?.().then( ( r: any ) => r.default || r );
              } else {
                func = middleware;
              }

              const result = await func( to, from );

              handleLayoutChange( meta );
              layoutChanged = true;

              if ( result ) {
                return result;
              }
            }
          }

          if ( !layoutChanged ) {
            handleLayoutChange( meta );
          }
        }
      }
    } catch ( error: any ) {
      headlessErrorState.value = {
        statusCode: ( error as FetchError ).statusCode,
        statusMessage: "Error fetching content for route: " + to.fullPath,
        cause: error,
      };
    }
  } else {
    headlessState.value = null;
  }

  if ( !headlessState.value ) {
    headlessErrorState.value = {
      statusCode: 404,
      statusMessage: "Page not found",
      data: to.fullPath,
    };
  }
} );
